import { Box, Card, Tooltip, Typography } from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  InfoOutlined,
} from "@mui/icons-material";
import { COLOR_CONSTANTS } from "../../../helpers/Colors";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { TOKENS } from "../constants/figmaTokens";
import { formatMoney } from "../utils/money";

const CARD_SX = {
  flex: "1 0 0",
  minWidth: 0,
  alignSelf: "stretch",
  bgcolor: TOKENS.surfaceDefault,
  borderRadius: "8px",
  p: "16px",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const COMPACT_CARD_SX = {
  bgcolor: TOKENS.surfaceDefault,
  borderRadius: "8px",
  p: "12px 16px",
  boxShadow: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
};

const TITLE_SX = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "16px",
  color: TOKENS.onSurfaceSecondary,
  textTransform: "uppercase",
};

const WHOLE_SX = {
  fontSize: 24,
  fontWeight: 700,
  lineHeight: "32px",
};

const DECIMAL_SX = {
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "20px",
};

const COMPACT_WHOLE_SX = {
  fontSize: 18,
  fontWeight: 700,
  lineHeight: "24px",
};

const COMPACT_DECIMAL_SX = {
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "16px",
};

const SUBTEXT_SX = {
  fontSize: 12,
  lineHeight: "16px",
  color: TOKENS.onSurfaceSecondary,
};

function OverviewCardShell({ title, tooltip, compact = false, children }) {
  const titleBlock = (
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
      <Typography sx={TITLE_SX}>{title}</Typography>
      <Tooltip title={tooltip} arrow placement="top">
        <InfoOutlined
          sx={{ fontSize: 14, color: TOKENS.onSurfaceSecondary }}
        />
      </Tooltip>
    </Box>
  );
  if (compact) {
    return (
      <Card sx={COMPACT_CARD_SX}>
        {titleBlock}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "4px",
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      </Card>
    );
  }
  return (
    <Card sx={CARD_SX}>
      {titleBlock}
      {children}
    </Card>
  );
}

const PILL_SX = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  height: "24px",
  px: "8px",
  borderRadius: "4px",
  color: "#FFFFFF",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "16px",
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  width: "fit-content",
};

function MatchPill({ variant }) {
  if (variant === "matched") {
    return (
      <Box
        sx={{ ...PILL_SX, bgcolor: COLOR_CONSTANTS["tertiary-green/variant"] }}
      >
        <CheckCircle sx={{ fontSize: 14 }} />
        3-way match
      </Box>
    );
  }
  return (
    <Box
      sx={{ ...PILL_SX, bgcolor: COLOR_CONSTANTS["tertiary-red/default"] }}
    >
      <ErrorIcon sx={{ fontSize: 14 }} />
      3-way mismatch
    </Box>
  );
}

function SingleValueRow({ value, accentGreen = false, compact = false }) {
  const [whole, decimal] = String(value).split(".");
  const color = accentGreen
    ? TOKENS.tertiaryGreenDefault
    : TOKENS.onSurfaceDefault;
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px", color }}>
      <Typography sx={compact ? COMPACT_WHOLE_SX : WHOLE_SX}>{whole}</Typography>
      {decimal !== undefined && (
        <Typography sx={compact ? COMPACT_DECIMAL_SX : DECIMAL_SX}>.{decimal}</Typography>
      )}
    </Box>
  );
}

// Hourglass + caption shared by the "absent" and "pending" GRN states so their
// styling can't drift apart.
function GrnStatusNote({ label }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", ...SUBTEXT_SX }}>
      <HourglassEmpty sx={{ fontSize: 14, color: TOKENS.onSurfaceSecondary }} />
      {label}
    </Box>
  );
}

function ThreeWayMatchState({ threeWayMatchState, grnValue, compact = false }) {
  if (threeWayMatchState === "absent") {
    return (
      <>
        <Typography sx={{ ...(compact ? COMPACT_WHOLE_SX : WHOLE_SX), color: TOKENS.onSurfaceDefault }}>
          —
        </Typography>
        <GrnStatusNote label="No GRN received yet" />
      </>
    );
  }
  return (
    <>
      <SingleValueRow value={formatMoney(grnValue ?? 0)} compact={compact} />
      {threeWayMatchState === "matched" && <MatchPill variant="matched" />}
      {threeWayMatchState === "mismatch" && <MatchPill variant="mismatch" />}
      {/* received but unallocated: show value, no match pill */}
      {threeWayMatchState === "pending" && (
        <GrnStatusNote label="GRN received, pending allocation" />
      )}
    </>
  );
}

function PaymentsCard({ paidAmount, poAmount, compact = false }) {
  const rawPct = poAmount > 0 ? (paidAmount / poAmount) * 100 : 0;
  const safePct = Math.max(0, Math.min(100, Math.round(rawPct)));
  const barColor =
    safePct >= 100
      ? TOKENS.tertiaryGreenDefault
      : safePct > 0
      ? COLOR_CONSTANTS["tertiary-yellow/default"]
      : TOKENS.surfaceVariant;
  if (compact) {
    return (
      <>
        <Typography sx={COMPACT_WHOLE_SX}>{safePct}% paid</Typography>
        <Typography sx={{ ...SUBTEXT_SX, textAlign: "right", overflowWrap: "anywhere" }}>
          {formatMoney(paidAmount ?? 0)}/{formatMoney(poAmount ?? 0)}
        </Typography>
      </>
    );
  }
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
        <Typography sx={WHOLE_SX}>{safePct}%</Typography>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            lineHeight: "24px",
            color: TOKENS.onSurfaceSecondary,
          }}
        >
          Of PO paid
        </Typography>
      </Box>
      <Box
        sx={{
          height: "1px",
          bgcolor: TOKENS.onSurfaceBorder,
          width: "100%",
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            flex: "1 0 0",
            height: "8px",
            bgcolor: TOKENS.surfaceVariant,
            borderRadius: "4px",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${safePct}%`,
              bgcolor: barColor,
              borderRadius: "4px",
            }}
          />
        </Box>
        <Typography sx={{ ...SUBTEXT_SX, whiteSpace: "nowrap" }}>
          {formatMoney(paidAmount ?? 0)}/{formatMoney(poAmount ?? 0)}
        </Typography>
      </Box>
    </>
  );
}

export default function PoOverviewCards({
  poAmount,
  invoicesRaisedAmount,
  invoiceCount,
  approvedAmount,
  approvedCount,
  grnValue,
  threeWayMatchState,
  paidAmount,
  showGrnCard = true,
}) {
  const compact = useIsMobile();
  const allApproved = invoiceCount > 0 && approvedCount === invoiceCount;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: compact ? "column" : "row",
        gap: compact ? "8px" : "16px",
        alignItems: "stretch",
      }}
    >
      <OverviewCardShell
        title="Total PO Amount"
        tooltip="Total value of this Purchase Order (including taxes)"
        compact={compact}
      >
        <SingleValueRow value={formatMoney(poAmount ?? 0)} compact={compact} />
      </OverviewCardShell>

      <OverviewCardShell
        title="Invoices Raised"
        tooltip="Sum of all invoices raised against this PO by the vendor"
        compact={compact}
      >
        <SingleValueRow value={formatMoney(invoicesRaisedAmount ?? 0)} compact={compact} />
        <Typography sx={SUBTEXT_SX}>
          {invoiceCount === 0
            ? "No invoices"
            : `from ${invoiceCount} invoice${invoiceCount === 1 ? "" : "s"}`}
        </Typography>
      </OverviewCardShell>

      <OverviewCardShell
        title="Approved Invoices"
        tooltip="Sum of invoices that have completed the approval flow"
        compact={compact}
      >
        <SingleValueRow
          value={formatMoney(approvedAmount ?? 0)}
          accentGreen={allApproved}
          compact={compact}
        />
        <Typography sx={SUBTEXT_SX}>
          {invoiceCount === 0
            ? "No invoices to approve"
            : `${approvedCount} of ${invoiceCount} approved`}
        </Typography>
      </OverviewCardShell>

      {showGrnCard && (
        <OverviewCardShell
          title="Total GRN Value"
          tooltip="Sum of all Goods Receipt Notes received against this PO"
          compact={compact}
        >
          <ThreeWayMatchState
            threeWayMatchState={threeWayMatchState}
            grnValue={grnValue}
            compact={compact}
          />
        </OverviewCardShell>
      )}

      <OverviewCardShell
        title="Payments Made"
        tooltip="Payments processed against approved invoices for this PO"
        compact={compact}
      >
        <PaymentsCard paidAmount={paidAmount ?? 0} poAmount={poAmount ?? 0} compact={compact} />
      </OverviewCardShell>
    </Box>
  );
}
